import { FC, FormEvent, useReducer } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/types'
import { setUserPreferences } from '../../store/userPreferences/actions'
import {
  arePreferencesLoaded,
  getUserPreferences
} from '../../store/userPreferences/selectors'
import {
  showTOBSetting,
  showTsySetting
} from '../../store/webSettings/selectors'
import CheckBoxLabelAndInput from '../CheckBoxLabelAndInput/CheckBoxLabelAndInput'
import Checkbox from '../Checkbox'
import ComponentFooter from '../ComponentFooter/ComponentFooter'
import ComponentHeader from '../ComponentHeader/ComponentHeader'
import { timeTitle } from '../DepthOfMarket/helpers'
import Modal from '../GenericModal/Modal'
import Loading from '../Loading/Loading'
import RadioButtonGroup, {
  RadioOption
} from '../RadioButtonGroup/RadioButtonGroup'
import {
  LastLookChoice,
  OptionalField,
  reducer,
  initWorkingPreferences,
  spotHedgeSelection,
  toPreferences
} from './reducer'
import styles from './settingsModal.module.scss'

interface Props {
  closeFunc: () => void
}

const lastLookOptions = [
  {
    label: 'In last look pop-out and activity feed',
    value: 'PopupAndActivityFeed'
  },
  { label: 'In activity feed only', value: 'InActivityFeed' },
  {
    label: "Don't ask for confirmation (automatically accept orders)",
    value: 'AutomaticallyAccept'
  }
] satisfies Array<RadioOption<LastLookChoice>>

const spotHedgeOptions = [
  { label: 'Auto-Spot', value: 'S' },
  { label: 'Auto-Hedge', value: 'H' }
] satisfies Array<RadioOption<string>>

const TraderPreferences: FC<Props> = ({ closeFunc }: Props) => {
  const showTsy = useAppSelector(showTsySetting)
  const showTOB = useAppSelector(showTOBSetting)
  const userPreferences = useAppSelector(getUserPreferences)
  const dispatch = useAppDispatch()

  const [workingPreferences, localDispatch] = useReducer(
    reducer,
    userPreferences,
    initWorkingPreferences
  )

  const optionalNumber = (
    field: OptionalField,
    labelCheckBox: string,
    testid: string
  ) => ({
    checked: workingPreferences[field].enabled,
    onChangeCheckBox: () => localDispatch({ type: 'toggleOptional', field }),
    inputValue: workingPreferences[field].text,
    onChangeInput: (e: FormEvent<HTMLInputElement>) =>
      localDispatch({
        type: 'setOptionalText',
        field,
        text: e.currentTarget.value
      }),
    labelCheckBox,
    testid,
    isNumber: true
  })

  const handleValidatePreferences = () => {
    dispatch(
      setUserPreferences(toPreferences(userPreferences, workingPreferences))
    )
    closeFunc()
  }

  return (
    <>
      <div className={styles.contentContainer}>
        <Checkbox
          locator="mute-notifications"
          onChange={() =>
            localDispatch({ type: 'toggle', field: 'muteNotifications' })
          }
          checked={workingPreferences.muteNotifications}
        >
          Mute notification sounds
        </Checkbox>
        <Checkbox
          locator="cancel-balance-upon-partial-trade"
          onChange={() =>
            localDispatch({
              type: 'toggle',
              field: 'cancelBalanceUponPartialTrade'
            })
          }
          checked={workingPreferences.cancelBalanceUponPartialTrade}
        >
          Cancel balance upon partial trade
        </Checkbox>
        <CheckBoxLabelAndInput
          {...optionalNumber(
            'minimumTradeSize',
            'Trade size minimum',
            'tradesize'
          )}
          otherContent="(k)"
        />
        <CheckBoxLabelAndInput
          {...optionalNumber(
            'maximumTradeSize',
            'Trade size maximum',
            'tradesizeMax'
          )}
          disabled={userPreferences.maximumTradeSizeIsHardLimit}
          otherContent="(k)"
        />
        <div className={styles.autoStart} title={timeTitle}>
          <Checkbox
            locator="use-openfin-autostart"
            onChange={() => localDispatch({ type: 'toggleAutoStart' })}
            checked={workingPreferences.openfinAutoStart}
          >
            Autostart OpenFin app at:
          </Checkbox>
          <input
            type="time"
            disabled={!workingPreferences.openfinAutoStart}
            value={workingPreferences.openfinStartTime}
            onChange={(e) =>
              localDispatch({
                type: 'setStartTime',
                time: e.currentTarget.value
              })
            }
            className={styles.timePicker}
            data-testid="openfin-start-time"
          />
        </div>

        <RadioButtonGroup
          legend="Ask for confirmation before accepting orders:"
          options={lastLookOptions}
          selectedOption={workingPreferences.lastLookChoice}
          onSelect={(choice) => localDispatch({ type: 'setLastLook', choice })}
          testPrefix="lastlook"
        />

        {showTsy && (
          <RadioButtonGroup
            legend="Auto-Spot/Hedge preference:"
            options={spotHedgeOptions}
            selectedOption={spotHedgeSelection(
              workingPreferences.defaultSpotHedgePreference
            )}
            onSelect={(value) => localDispatch({ type: 'setSpotHedge', value })}
            testPrefix="spotHedge"
          />
        )}
        <CheckBoxLabelAndInput
          {...optionalNumber(
            'defaultBidOfferValue',
            'Override Default Bid/Offer Size',
            'overrideDefaultBidOffer'
          )}
          otherContent="(k)"
        />
        {showTOB && (
          <>
            <CheckBoxLabelAndInput
              {...optionalNumber(
                'tobPriceIncrement',
                'TOB Price increment',
                'tobPriceIncrement'
              )}
            />
            <CheckBoxLabelAndInput
              {...optionalNumber(
                'tobSpreadIncrement',
                'TOB Spread increment',
                'tobSpreadIncrement'
              )}
            />
          </>
        )}
      </div>
      <ComponentFooter
        onCancelClick={closeFunc}
        onApplyClick={handleValidatePreferences}
        disableSubmit={!workingPreferences.isChanged}
      />
    </>
  )
}

// the reducer seeds once, so hold the mount until the stored preferences arrive
const SettingsModal: FC<Props> = ({ closeFunc }: Props) => {
  const loaded = useAppSelector(arePreferencesLoaded)
  return (
    <Modal customWrapperStyles={styles.settingsModal}>
      <ComponentHeader title="Trader Preferences" showInOpenFin />
      {loaded ? (
        <TraderPreferences closeFunc={closeFunc} />
      ) : (
        <>
          <Loading />
          <ComponentFooter onCancelClick={closeFunc} disableSubmit />
        </>
      )}
    </Modal>
  )
}

export default SettingsModal
